import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Hash, PhoneOff, Upload, UserCheck, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import {
  AdminAgent,
  ReservedCidRecord,
  ImportReservedCidPayload,
  apiService,
} from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';

/** Validates a single phone number against the E.164 format. */
const E164_REGEX = /^\+[1-9]\d{1,14}$/;

interface ValidationResult {
  valid: string[];
  errors: string[];
}

/** Parses a textarea value (one number per line) and validates each line. */
function parseAndValidateNumbers(raw: string): ValidationResult {
  const lines = raw
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const valid: string[] = [];
  const errors: string[] = [];

  lines.forEach((line, idx) => {
    if (E164_REGEX.test(line)) {
      valid.push(line);
    } else {
      errors.push(`Zeile ${idx + 1}: „${line}" ist kein gültiges E.164-Format (erwartet: +Ländervorwahl + Nummer, z.B. +4930123456).`);
    }
  });

  return { valid, errors };
}

export function CidPoolPage() {
  const [entries, setEntries] = useState<ReservedCidRecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Agent map for resolving takenBy UUIDs to names
  const [agentMap, setAgentMap] = useState<Map<string, AdminAgent>>(new Map());

  // Re-fetch trigger for agent-based data after assign/revoke actions
  const [agentsReloadKey, setAgentsReloadKey] = useState(0);

  // Assign dialog: backend-searched, paginated voice-agent picker (CA-472)
  const [agentPickerSearch, setAgentPickerSearch] = useState('');
  const [agentPickerPage, setAgentPickerPage] = useState(1);
  const [agentPickerResults, setAgentPickerResults] = useState<AdminAgent[]>([]);
  const [agentPickerTotalPages, setAgentPickerTotalPages] = useState(1);
  const [agentPickerLoading, setAgentPickerLoading] = useState(false);

  // "Agenten mit Rufnummer" card: backend-filtered + paginated (CA-472)
  const [numberAgents, setNumberAgents] = useState<AdminAgent[]>([]);
  const [numberAgentsSearch, setNumberAgentsSearch] = useState('');
  const [numberAgentsPage, setNumberAgentsPage] = useState(1);
  const [numberAgentsTotalPages, setNumberAgentsTotalPages] = useState(1);
  const [numberAgentsLoading, setNumberAgentsLoading] = useState(true);

  // Free import dialog state
  const [freeImportOpen, setFreeImportOpen] = useState(false);
  const [freeImporting, setFreeImporting] = useState(false);
  const [freeImportRaw, setFreeImportRaw] = useState('');
  const [freeImportErrors, setFreeImportErrors] = useState<string[]>([]);

  // Reserved import dialog state
  const [reservedImportOpen, setReservedImportOpen] = useState(false);
  const [reservedImporting, setReservedImporting] = useState(false);
  const [reservedImportRaw, setReservedImportRaw] = useState('');
  const [reservedImportComment, setReservedImportComment] = useState('');
  const [reservedImportErrors, setReservedImportErrors] = useState<string[]>([]);

  // Assign dialog state — opens via row button (number pre-selected) or "Bot zuweisen" (number selectable)
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignNumber, setAssignNumber] = useState('');
  const [assignAgent, setAssignAgent] = useState<AdminAgent | null>(null);
  const [assigning, setAssigning] = useState(false);

  // Impersonation state (one at a time, like AdminEventsTable)
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);

  // Release confirmation state
  const [releaseTarget, setReleaseTarget] = useState<ReservedCidRecord | null>(null);
  const [releasing, setReleasing] = useState(false);

  // Revoke confirmation state (agent-centric release — pool AND reserved numbers)
  const [revokeTarget, setRevokeTarget] = useState<AdminAgent | null>(null);
  const [revoking, setRevoking] = useState(false);

  const loadEntries = async (): Promise<void> => {
    setLoading(true);
    try {
      const result = await apiService.getReservedCids();
      setEntries(result.phonenumbers);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Rufnummern-Pool konnte nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  };

  /** Loads agents WITH a number for the takenBy name resolution (max. 200).
   *  Errors are tolerated silently — the table falls back to showing the UUID. */
  const loadAgentMap = async (): Promise<void> => {
    try {
      // takenBy verweist immer auf Voice-Bots mit Nummer — genau diese Menge laden
      const result = await apiService.getAdminAgentsPaginated({ type: 'voice', hasPhoneNumber: true, limit: 200 });
      const map = new Map<string, AdminAgent>();
      result.data.forEach((a) => { map.set(a.id, a); });
      setAgentMap(map);
    } catch {
      // Intentionally silent — UUID fallback is applied in the table
    }
  };

  useEffect(() => {
    void loadEntries();
  }, []);

  useEffect(() => {
    void loadAgentMap();
  }, [agentsReloadKey]);

  // Backend-Suche für den Agent-Picker im Zuweisen-Dialog (debounced)
  useEffect(() => {
    if (!assignOpen) return;
    const timer = setTimeout(() => {
      setAgentPickerLoading(true);
      apiService
        .getAdminAgentsPaginated({
          type: 'voice',
          search: agentPickerSearch.trim() || undefined,
          page: agentPickerPage,
          limit: 10,
        })
        .then((result) => {
          setAgentPickerResults(result.data);
          setAgentPickerTotalPages(result.pagination.totalPages || 1);
        })
        .catch(() => { toast.error('Voice-Agenten konnten nicht geladen werden.'); })
        .finally(() => { setAgentPickerLoading(false); });
    }, 300);
    return () => { clearTimeout(timer); };
  }, [assignOpen, agentPickerSearch, agentPickerPage]);

  // Backend-Filterung für die Karte „Agenten mit Rufnummer" (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      setNumberAgentsLoading(true);
      apiService
        .getAdminAgentsPaginated({
          type: 'voice',
          hasPhoneNumber: true,
          search: numberAgentsSearch.trim() || undefined,
          page: numberAgentsPage,
          limit: 10,
        })
        .then((result) => {
          setNumberAgents(result.data);
          setNumberAgentsTotalPages(result.pagination.totalPages || 1);
        })
        .catch(() => { toast.error('Agenten mit Rufnummer konnten nicht geladen werden.'); })
        .finally(() => { setNumberAgentsLoading(false); });
    }, 300);
    return () => { clearTimeout(timer); };
  }, [numberAgentsSearch, numberAgentsPage, agentsReloadKey]);

  /** Returns the agent display name for a takenBy UUID, or the UUID itself as fallback. */
  const resolveAgentName = (takenBy: string): { label: string; isResolved: boolean } => {
    const agent = agentMap.get(takenBy);
    if (agent) {
      return { label: agent.name, isResolved: true };
    }
    return { label: takenBy, isResolved: false };
  };

  // Free reserved numbers — selectable in the assign dialog
  const freeReservedNumbers = entries.filter((e) => !e.taken);

  // ── Free import ───────────────────────────────────────────────────

  const resetFreeImport = (): void => {
    setFreeImportRaw('');
    setFreeImportErrors([]);
  };

  const handleFreeImport = async (e: React.SyntheticEvent): Promise<void> => {
    e.preventDefault();
    const { valid, errors } = parseAndValidateNumbers(freeImportRaw);
    if (errors.length > 0) {
      setFreeImportErrors(errors);
      return;
    }
    if (valid.length === 0) {
      toast.error('Bitte mindestens eine Telefonnummer eingeben.');
      return;
    }
    setFreeImporting(true);
    try {
      const result = await apiService.importCidPoolNumbers(valid);
      const skippedInfo =
        result.skipped.length > 0
          ? ` Übersprungen (bereits vorhanden): ${result.skipped.join(', ')}.`
          : '';
      toast.success(`${result.imported.toString()} Nummer(n) importiert.${skippedInfo}`);
      setFreeImportOpen(false);
      resetFreeImport();
      void loadEntries();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Import fehlgeschlagen.'));
    } finally {
      setFreeImporting(false);
    }
  };

  // ── Reserved import ───────────────────────────────────────────────

  const resetReservedImport = (): void => {
    setReservedImportRaw('');
    setReservedImportComment('');
    setReservedImportErrors([]);
  };

  const handleReservedImport = async (e: React.SyntheticEvent): Promise<void> => {
    e.preventDefault();
    const comment = reservedImportComment.trim();
    if (!comment) {
      toast.error('Bitte einen Kommentar für diese Reservierung eingeben.');
      return;
    }
    const { valid, errors } = parseAndValidateNumbers(reservedImportRaw);
    if (errors.length > 0) {
      setReservedImportErrors(errors);
      return;
    }
    if (valid.length === 0) {
      toast.error('Bitte mindestens eine Telefonnummer eingeben.');
      return;
    }
    const payload: ImportReservedCidPayload[] = valid.map((phonenumber) => ({
      phonenumber,
      comment,
    }));
    setReservedImporting(true);
    try {
      const result = await apiService.importReservedCidPoolNumbers(payload);
      const skippedInfo =
        result.skipped.length > 0
          ? ` Übersprungen (bereits vorhanden): ${result.skipped.join(', ')}.`
          : '';
      toast.success(
        `${result.imported.toString()} reservierte Nummer(n) importiert.${skippedInfo}`,
      );
      setReservedImportOpen(false);
      resetReservedImport();
      void loadEntries();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Import fehlgeschlagen.'));
    } finally {
      setReservedImporting(false);
    }
  };

  // ── Assign ────────────────────────────────────────────────────────

  const resetAssign = (): void => {
    setAssignNumber('');
    setAssignAgent(null);
    setAgentPickerSearch('');
    setAgentPickerPage(1);
  };

  const openAssignDialog = (phonenumber?: string): void => {
    resetAssign();
    if (phonenumber) setAssignNumber(phonenumber);
    setAssignOpen(true);
  };

  const handleAssign = async (e: React.SyntheticEvent): Promise<void> => {
    e.preventDefault();
    if (!assignNumber) {
      toast.error('Bitte eine Telefonnummer auswählen.');
      return;
    }
    if (!assignAgent) {
      toast.error('Bitte einen Voice-Agenten auswählen.');
      return;
    }
    setAssigning(true);
    try {
      const result = await apiService.assignNumberToBot(assignAgent.id, assignNumber);
      toast.success(`Nummer ${result.cid} wurde dem Bot „${assignAgent.name}" zugewiesen.`);
      setAssignOpen(false);
      resetAssign();
      void loadEntries();
      setAgentsReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Zuweisung fehlgeschlagen.'));
    } finally {
      setAssigning(false);
    }
  };

  // ── Impersonation (wie AdminEventsTable) ─────────────────────────

  const handleImpersonate = async (agent: AdminAgent): Promise<void> => {
    if (impersonatingId) return;
    setImpersonatingId(agent.customerId);
    try {
      const data = await apiService.startImpersonation(
        agent.customerId,
        `Quick impersonation from CID pool - Agent ${agent.name}`,
      );
      const customerPortalUrl = import.meta.env.VITE_CUSTOMER_PORTAL_URL || 'http://app.callbert.localhost:3005/';
      window.open(`${customerPortalUrl}?impersonation_token=${data.token}`, '_blank', 'noopener,noreferrer');
      toast.success(`Impersonation gestartet für ${agent.customerName ?? agent.customerId}`, {
        description: 'Customer Portal in neuem Tab geöffnet',
        duration: 4000,
      });
    } catch {
      toast.error('Fehler beim Starten der Impersonation', { duration: 5000 });
    } finally {
      setImpersonatingId(null);
    }
  };

  // ── Release ───────────────────────────────────────────────────────

  const handleRelease = async (): Promise<void> => {
    if (!releaseTarget?.takenBy) return;
    setReleasing(true);
    try {
      await apiService.releaseReservedCid(releaseTarget.takenBy);
      toast.success(
        `Nummer ${releaseTarget.phonenumber} wurde freigegeben und ist wieder im Pool verfügbar.`,
      );
      setReleaseTarget(null);
      void loadEntries();
      setAgentsReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Freigabe fehlgeschlagen.'));
    } finally {
      setReleasing(false);
    }
  };

  // ── Revoke (agent-centric) ────────────────────────────────────────

  const handleRevoke = async (): Promise<void> => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await apiService.releaseAgentPhoneNumber(revokeTarget.id);
      toast.success(
        `Nummer ${revokeTarget.phoneNumber ?? ''} wurde dem Agenten „${revokeTarget.name}" entzogen.`,
      );
      setRevokeTarget(null);
      void loadEntries();
      setAgentsReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Nummer konnte nicht entzogen werden.'));
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Hash className="w-6 h-6 text-indigo-600" />
            Rufnummern-Pool
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Reservierte Nummern werden niemals automatisch vergeben — Zuweisung erfolgt
            ausschließlich manuell. Nur Super-Admins haben Zugriff auf diese Seite.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => { setFreeImportOpen(true); }}
          >
            <Upload className="w-4 h-4 mr-2" />
            Nummern importieren
          </Button>
          <Button
            variant="outline"
            onClick={() => { setReservedImportOpen(true); }}
          >
            <Upload className="w-4 h-4 mr-2" />
            Reservierte importieren
          </Button>
        </div>
      </div>

      {/* Assign entry point — direkt über der Reserved-Tabelle */}
      <div className="flex justify-end">
        <Button onClick={() => { openAssignDialog(); }}>
          <UserPlus className="w-4 h-4 mr-2" />
          Bot zuweisen
        </Button>
      </div>

      {/* Reserved numbers table */}
      <Card>
        <CardHeader>
          <CardTitle>Reservierte Rufnummern</CardTitle>
          <CardDescription>
            Nummern, die für bestimmte Kunden oder Bots vorgehalten werden. Eine reservierte
            Nummer kann nur über „Bot zuweisen" vergeben werden.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-sm text-gray-500">Lade Rufnummern-Pool…</div>
          ) : entries.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">
              Keine reservierten Nummern vorhanden. Klicke „Reservierte importieren", um Nummern
              hinzuzufügen.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Telefonnummer</TableHead>
                  <TableHead>Kommentar</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Vergeben an</TableHead>
                  <TableHead className="text-right">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => {
                  const takenByResolution =
                    entry.takenBy ? resolveAgentName(entry.takenBy) : null;
                  return (
                    <TableRow key={entry.phonenumber}>
                      <TableCell className="font-mono">{entry.phonenumber}</TableCell>
                      <TableCell className="text-gray-600">{entry.comment ?? '—'}</TableCell>
                      <TableCell>
                        {entry.taken ? (
                          <Badge variant="default">vergeben</Badge>
                        ) : (
                          <Badge variant="secondary">frei</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {takenByResolution ? (
                          takenByResolution.isResolved ? (
                            <span className="inline-flex items-center gap-1">
                              <span
                                title={entry.takenBy ?? undefined}
                                className="text-gray-900"
                              >
                                {takenByResolution.label}
                              </span>
                              {entry.takenBy && agentMap.get(entry.takenBy) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  title={`Als ${agentMap.get(entry.takenBy)?.customerName ?? 'Kunde'} anmelden (Impersonation)`}
                                  disabled={impersonatingId !== null}
                                  onClick={() => {
                                    const agent = entry.takenBy ? agentMap.get(entry.takenBy) : undefined;
                                    if (agent) void handleImpersonate(agent);
                                  }}
                                  aria-label={`Impersonation für Kunde von ${takenByResolution.label} starten`}
                                >
                                  <UserCheck className="w-4 h-4" />
                                </Button>
                              )}
                            </span>
                          ) : (
                            <span className="font-mono text-gray-600">
                              {takenByResolution.label}
                            </span>
                          )
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {!entry.taken ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { openAssignDialog(entry.phonenumber); }}
                            aria-label={`Bot zuweisen für ${entry.phonenumber}`}
                          >
                            <UserPlus className="w-4 h-4 mr-1" />
                            Bot zuweisen
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setReleaseTarget(entry); }}
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            aria-label={`Nummer ${entry.phonenumber} freigeben`}
                          >
                            Freigeben
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Agents with assigned numbers — backend-filtered + paginated (CA-472) */}
      <Card>
        <CardHeader>
          <CardTitle>Agenten mit Rufnummer</CardTitle>
          <CardDescription>
            Voice-Agenten, denen aktuell eine Rufnummer zugewiesen ist — aus dem freien Pool
            oder reserviert. „Nummer entziehen" trennt die Nummer vom Agenten; reservierte
            Nummern fallen reserviert in den Pool zurück.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            placeholder="Nach Name oder Agent-ID suchen…"
            value={numberAgentsSearch}
            onChange={(e) => {
              setNumberAgentsSearch(e.target.value);
              setNumberAgentsPage(1);
            }}
            aria-label="Agenten mit Rufnummer durchsuchen"
          />
          {numberAgentsLoading ? (
            <div className="py-12 text-center text-sm text-gray-500">Lade Agenten…</div>
          ) : numberAgents.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">
              {numberAgentsSearch.trim()
                ? 'Keine Agenten mit Rufnummer für diese Suche gefunden.'
                : 'Kein Voice-Agent hat aktuell eine Rufnummer zugewiesen.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Telefonnummer</TableHead>
                  <TableHead className="text-right">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {numberAgents.map((agent) => (
                  <TableRow key={agent.id}>
                    <TableCell>
                      <span className="block text-gray-900">{agent.name}</span>
                      <span className="block font-mono text-xs text-gray-500">{agent.id}</span>
                    </TableCell>
                    <TableCell className="text-gray-600">{agent.customerName ?? '—'}</TableCell>
                    <TableCell className="font-mono">{agent.phoneNumber}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setRevokeTarget(agent); }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        aria-label={`Nummer ${agent.phoneNumber ?? ''} von ${agent.name} entziehen`}
                      >
                        <PhoneOff className="w-4 h-4 mr-1" />
                        Nummer entziehen
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={numberAgentsPage <= 1 || numberAgentsLoading}
              onClick={() => { setNumberAgentsPage((p) => p - 1); }}
            >
              Zurück
            </Button>
            <span className="text-xs text-gray-500">
              Seite {numberAgentsPage} von {numberAgentsTotalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={numberAgentsPage >= numberAgentsTotalPages || numberAgentsLoading}
              onClick={() => { setNumberAgentsPage((p) => p + 1); }}
            >
              Weiter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Free import dialog */}
      <Dialog
        open={freeImportOpen}
        onOpenChange={(open) => {
          setFreeImportOpen(open);
          if (!open) resetFreeImport();
        }}
      >
        <DialogContent>
          <form onSubmit={(e) => { void handleFreeImport(e); }}>
            <DialogHeader>
              <DialogTitle>Nummern importieren</DialogTitle>
              <DialogDescription>
                Frei vergebbare Nummern in den Pool aufnehmen. Bereits vorhandene Nummern werden
                übersprungen. Format: E.164 mit führendem „+" (z.B. +4930123456), eine Nummer
                pro Zeile.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="free-import-numbers">Telefonnummern</Label>
                <Textarea
                  id="free-import-numbers"
                  placeholder={'+4930123456\n+4989654321\n+41441234567'}
                  value={freeImportRaw}
                  onChange={(e) => {
                    setFreeImportRaw(e.target.value);
                    setFreeImportErrors([]);
                  }}
                  rows={6}
                  autoFocus
                />
                <p className="text-xs text-gray-500">Eine Nummer pro Zeile, E.164-Format.</p>
              </div>
              {freeImportErrors.length > 0 && (
                <ul className="space-y-1 text-sm text-red-600" role="alert">
                  {freeImportErrors.map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setFreeImportOpen(false); }}
              >
                Abbrechen
              </Button>
              <Button type="submit" disabled={freeImporting}>
                {freeImporting ? 'Wird importiert…' : 'Importieren'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reserved import dialog */}
      <Dialog
        open={reservedImportOpen}
        onOpenChange={(open) => {
          setReservedImportOpen(open);
          if (!open) resetReservedImport();
        }}
      >
        <DialogContent>
          <form onSubmit={(e) => { void handleReservedImport(e); }}>
            <DialogHeader>
              <DialogTitle>Reservierte Nummern importieren</DialogTitle>
              <DialogDescription>
                Nummern als reserviert kennzeichnen und mit einem Kommentar versehen. Reservierte
                Nummern werden nie automatisch vergeben. Bereits vorhandene Nummern werden
                übersprungen.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reserved-import-comment">
                  Kommentar <span className="text-red-500" aria-hidden="true">*</span>
                </Label>
                <Input
                  id="reserved-import-comment"
                  placeholder="z.B. reserviert für Kunde Muster GmbH"
                  value={reservedImportComment}
                  onChange={(e) => { setReservedImportComment(e.target.value); }}
                  maxLength={500}
                  required
                  autoFocus
                />
                <p className="text-xs text-gray-500">
                  Gilt für alle Nummern dieses Imports. Maximal 500 Zeichen.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reserved-import-numbers">Telefonnummern</Label>
                <Textarea
                  id="reserved-import-numbers"
                  placeholder={'+4930123456\n+4989654321\n+41441234567'}
                  value={reservedImportRaw}
                  onChange={(e) => {
                    setReservedImportRaw(e.target.value);
                    setReservedImportErrors([]);
                  }}
                  rows={6}
                />
                <p className="text-xs text-gray-500">Eine Nummer pro Zeile, E.164-Format.</p>
              </div>
              {reservedImportErrors.length > 0 && (
                <ul className="space-y-1 text-sm text-red-600" role="alert">
                  {reservedImportErrors.map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setReservedImportOpen(false); }}
              >
                Abbrechen
              </Button>
              <Button type="submit" disabled={reservedImporting}>
                {reservedImporting ? 'Wird importiert…' : 'Reserviert importieren'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog
        open={assignOpen}
        onOpenChange={(open) => {
          setAssignOpen(open);
          if (!open) resetAssign();
        }}
      >
        <DialogContent>
          <form onSubmit={(e) => { void handleAssign(e); }}>
            <DialogHeader>
              <DialogTitle>Bot zuweisen</DialogTitle>
              <DialogDescription>
                Die ausgewählte Nummer wird dem Voice-Bot über einen LiveKit-Trunk und eine
                Dispatch-Rule zugewiesen. Dies ist der einzige Weg, eine reservierte Nummer zu
                vergeben.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="assign-number-select">
                  Telefonnummer <span className="text-red-500" aria-hidden="true">*</span>
                </Label>
                <Select
                  value={assignNumber}
                  onValueChange={setAssignNumber}
                >
                  <SelectTrigger id="assign-number-select" className="font-mono">
                    <SelectValue placeholder="Nummer auswählen…" />
                  </SelectTrigger>
                  <SelectContent>
                    {freeReservedNumbers.length === 0 ? (
                      <div className="px-2 py-3 text-sm text-gray-500 text-center">
                        Keine freien reservierten Nummern im Pool. Erst über „Reservierte
                        importieren" Nummern aufnehmen.
                      </div>
                    ) : (
                      freeReservedNumbers.map((entry) => (
                        <SelectItem key={entry.phonenumber} value={entry.phonenumber}>
                          {entry.phonenumber}
                          {entry.comment ? ` — ${entry.comment}` : ''}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Zur Auswahl stehen alle freien reservierten Nummern des Pools.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="assign-agent-search">
                  Voice-Agent <span className="text-red-500" aria-hidden="true">*</span>
                </Label>
                <Input
                  id="assign-agent-search"
                  placeholder="Nach Name oder Agent-ID suchen…"
                  value={agentPickerSearch}
                  onChange={(e) => {
                    setAgentPickerSearch(e.target.value);
                    setAgentPickerPage(1);
                  }}
                />
                <div
                  className="rounded-md border divide-y max-h-56 overflow-y-auto"
                  role="listbox"
                  aria-label="Voice-Agenten"
                >
                  {agentPickerLoading ? (
                    <div className="px-3 py-4 text-sm text-gray-500 text-center">Lade Agenten…</div>
                  ) : agentPickerResults.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-gray-500 text-center">
                      Keine Voice-Agenten gefunden.
                    </div>
                  ) : (
                    agentPickerResults.map((agent) => (
                      <button
                        key={agent.id}
                        type="button"
                        role="option"
                        aria-selected={assignAgent?.id === agent.id}
                        onClick={() => { setAssignAgent(agent); }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                          assignAgent?.id === agent.id ? 'bg-indigo-50' : ''
                        }`}
                      >
                        <span className="block text-gray-900">
                          {agent.name}
                          {agent.customerName ? ` · ${agent.customerName}` : ''}
                        </span>
                        <span className="block font-mono text-xs text-gray-500">{agent.id}</span>
                      </button>
                    ))
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={agentPickerPage <= 1 || agentPickerLoading}
                    onClick={() => { setAgentPickerPage((p) => p - 1); }}
                  >
                    Zurück
                  </Button>
                  <span className="text-xs text-gray-500">
                    Seite {agentPickerPage} von {agentPickerTotalPages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={agentPickerPage >= agentPickerTotalPages || agentPickerLoading}
                    onClick={() => { setAgentPickerPage((p) => p + 1); }}
                  >
                    Weiter
                  </Button>
                </div>
                {assignAgent && (
                  <p className="text-xs text-gray-600">
                    Ausgewählt: <strong>{assignAgent.name}</strong>{' '}
                    (<span className="font-mono">{assignAgent.id}</span>)
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  Suche filtert backendseitig nach Name oder Agent-ID. Die Nummer wird per
                  LiveKit-Trunk und Dispatch-Rule an diesen Bot gebunden. Nur Voice-Agenten.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAssignOpen(false);
                  resetAssign();
                }}
              >
                Abbrechen
              </Button>
              <Button type="submit" disabled={assigning || !assignAgent || !assignNumber}>
                {assigning ? 'Wird zugewiesen…' : 'Zuweisen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Release confirmation */}
      <AlertDialog
        open={!!releaseTarget}
        onOpenChange={(open) => { if (!open) setReleaseTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Nummer freigeben?</AlertDialogTitle>
            <AlertDialogDescription>
              {releaseTarget && (
                <>
                  <strong className="font-mono">{releaseTarget.phonenumber}</strong> wird vom Bot{' '}
                  <span className="font-mono">
                    {releaseTarget.takenBy
                      ? (agentMap.get(releaseTarget.takenBy)?.name ?? releaseTarget.takenBy)
                      : ''}
                  </span>{' '}
                  getrennt. Die Nummer bleibt reserviert und fällt in den Pool zurück — der
                  Kommentar bleibt erhalten.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={releasing}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleRelease();
              }}
              disabled={releasing}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {releasing ? 'Wird freigegeben…' : 'Freigeben'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke confirmation (agent-centric) */}
      <AlertDialog
        open={!!revokeTarget}
        onOpenChange={(open) => { if (!open) setRevokeTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Nummer entziehen?</AlertDialogTitle>
            <AlertDialogDescription>
              {revokeTarget && (
                <>
                  <strong className="font-mono">{revokeTarget.phoneNumber}</strong> wird dem
                  Agenten <strong>{revokeTarget.name}</strong> entzogen. Der Agent ist danach
                  nicht mehr telefonisch erreichbar. Reservierte Nummern fallen reserviert in
                  den Pool zurück, freie Nummern sind wieder automatisch vergebbar.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleRevoke();
              }}
              disabled={revoking}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {revoking ? 'Wird entzogen…' : 'Nummer entziehen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
